/* eslint-disable */
import { Component, effect, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FileSystemService } from '../../services/file-system.service';
import { AgGridModule } from 'ag-grid-angular';
import { ColDef, GridReadyEvent, GridOptions, RowClassParams, RowStyle } from 'ag-grid-community';
import * as Papa from 'papaparse';
import { AiService } from '../../services/ai/ai.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-dataviewer',
  templateUrl: './dataviewer.component.html',
  styleUrls: ['./dataviewer.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    AgGridModule
  ]
})
export class DataviewerComponent implements OnDestroy {
  // AG Grid properties
  columnDefs: ColDef[] = [];
  rowData: unknown[] = [];
  defaultColDef: ColDef = {
    sortable: true,
    resizable: true,
    filter: true,
    editable: true,
    wrapText: true,
    autoHeight: true,
    cellStyle: { textAlign: 'center', borderRight: '2px solid #c0c0c0' },
    headerClass: 'ag-header-cell-centered',
    filterParams: {
      buttons: ['reset', 'apply'],
      closeOnApply: false,
    },
    headerComponentParams: {
      menuIcon: 'fa-thumbtack',
      onMenuClick: this.onHeaderRightClick.bind(this)
    }
  };

  // Add grid options to show row numbers
  gridOptions: GridOptions = {
    rowSelection: 'multiple' as const,
    suppressRowClickSelection: true,
    getRowId: (params: any) => params.id,
    statusBar: {
      statusPanels: [
        { statusPanel: 'agTotalRowCountComponent', align: 'left' },
        { statusPanel: 'agFilteredRowCountComponent' }
      ]
    }
  };

  // Grid APIs
  private gridApi: any;
  private gridColumnApi: any;

  // Data storage
  dataHeaders: string[] = [];
  dataRows: any[] = [];

  // Add loading state
  isLoading: boolean = true;

  private aiIsWritingSubscription: Subscription;

  // Add a property to store the color column name
  colorColumnName: string | null = null;
  
  // Function to apply row styles based on color column with proper typing
  getRowStyle = (params: RowClassParams): RowStyle | undefined => {
    if (this.colorColumnName && params.data && params.data[this.colorColumnName]) {
      const colorValue = params.data[this.colorColumnName];
      // Check if the color value is a valid CSS color
      if (this.isValidColor(colorValue)) {
        return { 
          background: colorValue,
          // Add text color adjustment for better readability
          color: this.getContrastColor(colorValue)
        };
      }
    }
    return undefined;
  };

  constructor(public fileSystemService: FileSystemService, public aiService: AiService) {
    effect(() => {
      this.isLoading = true;
      this.fileSystemService.visibleNode().getContent().then((content) => {
        if (content) {
          this.parseCSV(content);
        }
        this.isLoading = false;
      }).catch(error => {
        console.error('Error loading content:', error);
        this.isLoading = false;
      });
    });

    // Subscribe to aiIsWriting$ to update the editable property
    this.aiIsWritingSubscription = this.aiService.aiIsWriting$.subscribe(isWriting => {
      this.defaultColDef.editable = !isWriting;
      if (this.gridApi) {
        this.gridApi.refreshCells({ force: true });
        this.gridApi.setColumnDefs(this.columnDefs);
      }
    });
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    
    // Resize columns immediately when grid is ready
    this.gridColumnApi.autoSizeAllColumns(false);
    
    // Mark as loaded once grid is ready
    this.isLoading = false;
    
    // Resize grid after a short delay to ensure container is fully rendered
    setTimeout(() => {
      this.gridApi.sizeColumnsToFit();
      this.gridColumnApi.autoSizeAllColumns(false);
    }, 100);
    
    // Create a ResizeObserver to handle container size changes
    const gridElement = document.querySelector('.ag-theme-alpine');
    if (gridElement) {
      const resizeObserver = new ResizeObserver(() => {
        setTimeout(() => {
          this.gridApi.sizeColumnsToFit();
        }, 50);
      });
      resizeObserver.observe(gridElement);
    }
    
    // Add context menu event listener to handle right-clicks on headers
    if (gridElement) {
      gridElement.addEventListener('contextmenu', (this.handleContextMenu.bind(this) as EventListener));
    }
  }

  parseCSV(content: string): void {
    this.isLoading = true;
    
    const parseResult = Papa.parse<any>(content, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true
    });
    
    if (parseResult.data.length === 0) {
      this.isLoading = false;
      return;
    }

    // Add unique IDs to each row to ensure proper rendering
    this.dataRows = parseResult.data.map((row: any, index) => {
      return { ...row, __id: index.toString() };
    });
    
    this.dataHeaders = Object.keys(parseResult.data[0]);
        
    // Detect color column - look for columns named 'color', 'colour', 'backgroundColor', etc.
    this.detectColorColumn(this.dataHeaders);
    
    const columnWidths = this.calculateOptimalColumnWidths(this.dataHeaders, this.dataRows);
    
    // Add row number column first
    this.columnDefs = [
      {
        headerName: '#',
        valueGetter: 'node.rowIndex + 1',
        width: 70,
        pinned: 'left',
        suppressMovable: true,
        cellClass: 'row-number-cell',
        filter: false,
        sortable: false,
        resizable: false,
        editable: false
      }
    ];
    
    // Then add the data columns (excluding the color column)
    this.columnDefs = this.columnDefs.concat(
      this.dataHeaders
        .filter(header => header !== this.colorColumnName) // Filter out the color column
        .map(header => ({
          field: header,
          headerName: header,
          autoHeaderHeight: true,
          wrapText: true,
          autoHeight: true,
          width: columnWidths[header],
          maxWidth: 300,
          minWidth: columnWidths[header],
          suppressSizeToFit: true,
          headerClass: 'header-cell-full-width'
        }))
    );
    
    // Update grid options to use the ID field
    this.gridOptions = {
      ...this.gridOptions,
      getRowId: (params) => params.data.__id
    };
    
    // Set row data
    this.rowData = [...this.dataRows];
    
    // Force grid refresh after data is loaded
    if (this.gridApi) {
      // Reset scroll position to top
      this.gridApi.ensureIndexVisible(0);
      this.gridApi.setRowData(this.rowData);
      this.gridApi.redrawRows();
    }
    
    this.isLoading = false;
    
    if (parseResult.errors.length > 0) {
      console.warn('CSV parsing errors:', parseResult.errors);
    }
  }

  private calculateOptimalColumnWidths(headers: string[], rows: any[]): { [key: string]: number } {
    const widths: { [key: string]: number } = {};
    const minWidth = 80;
    const maxWidth = 300;
    const charWidth = 8;
    const padding = 20;
    
    headers.forEach(header => {
      const headerWidth = Math.min((header.length * charWidth) + padding, maxWidth);
      
      let contentMaxWidth = 0;
      const sampleRows = rows.slice(0, 100);
      
      sampleRows.forEach(row => {
        const cellValue = row[header];
        if (cellValue !== null && cellValue !== undefined) {
          const cellValueStr = String(cellValue);
          const contentWidth = Math.min((cellValueStr.length * charWidth) + padding, maxWidth);
          contentMaxWidth = Math.max(contentMaxWidth, contentWidth);
        }
      });
      
      contentMaxWidth = Math.min(contentMaxWidth, maxWidth);
      widths[header] = Math.min(Math.max(headerWidth, contentMaxWidth, minWidth), maxWidth);
    });
    
    return widths;
  }

  resetFilters(): void {
    if (this.gridApi) {
      this.gridApi.setFilterModel(null);
      this.gridApi.onFilterChanged();
    }
  }

  // Handle right-click on header cells
  handleContextMenu(event: Event): void {
    // Cast the event to MouseEvent to access MouseEvent properties
    const mouseEvent = event as MouseEvent;
    
    // Check if the right-click is on a header cell
    const target = mouseEvent.target as HTMLElement;
    const headerCell = target.closest('.ag-header-cell');
    
    if (headerCell) {
      // Prevent the default context menu
      mouseEvent.preventDefault();
      
      // Get the column ID from the header cell
      const colId = headerCell.getAttribute('col-id');
      if (colId) {
        this.toggleColumnPin(colId);
      }
    }
  }

  // Toggle column pin state
  toggleColumnPin(colId: string): void {
    if (!this.gridColumnApi) return;
    
    const column = this.gridColumnApi.getColumn(colId);
    if (!column) return;
    
    const pinned = column.getPinned();
    
    if (pinned) {
      // If already pinned, unpin it
      this.gridColumnApi.setColumnPinned(colId, null);
    } else {
      // If not pinned, pin it to the left
      this.gridColumnApi.setColumnPinned(colId, 'left');
    }
  }

  // Optional: Method to handle header menu click (alternative to context menu)
  onHeaderRightClick(params: any): void {
    const colId = params.column.getColId();
    this.toggleColumnPin(colId);
  }

  // Helper method to detect color column
  private detectColorColumn(headers: string[]): void {
    // Common names for color columns
    const colorColumnNames = ['##COLOR##'];
    
    // First check for exact matches
    for (const name of colorColumnNames) {
      if (headers.includes(name)) {
        this.colorColumnName = name;
        return;
      }
    }
    
    // If no exact match, check for partial matches (case insensitive)
    for (const header of headers) {
      const headerLower = header.toLowerCase();
      if (colorColumnNames.some(name => headerLower.includes(name.toLowerCase()))) {
        this.colorColumnName = header;
        return;
      }
    }
    
    // No color column found - remove the auto-detection logic
    this.colorColumnName = null;
  }
  
  // Helper method to check if a string is a valid CSS color
  private isValidColor(color: string): boolean {
    if (!color || typeof color !== 'string') return false;
    
    // Check for hex colors
    if (/^#([0-9A-F]{3}){1,2}$/i.test(color)) return true;
    
    // Check for rgb/rgba colors
    if (/^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/i.test(color) || 
        /^rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*[\d.]+\s*\)$/i.test(color)) return true;
    
    // Check for named colors by creating a test element
    const testEl = document.createElement('div');
    testEl.style.color = color;
    return !!testEl.style.color;
  }
  
  // Helper method to determine contrasting text color for better readability
  private getContrastColor(hexColor: string): string {
    // Default to white if we can't determine
    if (!hexColor || typeof hexColor !== 'string') return '#ffffff';
    
    let color = hexColor.trim();
    
    // Convert shorthand hex (#fff) to full form (#ffffff)
    if (/^#([0-9A-F]{3})$/i.test(color)) {
      color = '#' + color[1] + color[1] + color[2] + color[2] + color[3] + color[3];
    }
    
    // Handle hex colors
    if (/^#[0-9A-F]{6}$/i.test(color)) {
      const r = parseInt(color.substring(1, 3), 16);
      const g = parseInt(color.substring(3, 5), 16);
      const b = parseInt(color.substring(5, 7), 16);
      
      // Calculate luminance - brighter colors have higher values
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      
      // Use white text for dark backgrounds, black text for light backgrounds
      return luminance > 0.5 ? '#000000' : '#ffffff';
    }
    
    // For non-hex colors, default to white which works in most cases
    return '#ffffff';
  }

  ngOnDestroy() {
    if (this.aiIsWritingSubscription) {
      this.aiIsWritingSubscription.unsubscribe();
    }
  }
}